import {createSlice} from '@reduxjs/toolkit';
import {
    getAccountMcpServiceAccountFailure,
    getAccountMcpServiceAccountRequest,
    getAccountMcpServiceAccountSuccess,
} from 'src/openapi/admin/apis/GetAccountMcpServiceAccountApi';
import {
    getMcpCreditAlertSettingsFailure,
    getMcpCreditAlertSettingsRequest,
    getMcpCreditAlertSettingsSuccess,
    saveMcpCreditAlertSettingsFailure,
    saveMcpCreditAlertSettingsRequest,
    saveMcpCreditAlertSettingsSuccess,
} from 'src/openapi/admin/apis/McpAlertSettingsApi';
import {
    createMcpContractFailure,
    createMcpContractRequest,
    createMcpContractSuccess,
    getMcpContractsFailure,
    getMcpContractsRequest,
    getMcpContractsSuccess,
    updateMcpContractCreditsFailure,
    updateMcpContractCreditsRequest,
    updateMcpContractCreditsSuccess,
    updateMcpContractFailure,
    updateMcpContractRequest,
    updateMcpContractSuccess,
} from 'src/openapi/admin/apis/McpContractsApi';
import {
    updateAccountMcpServiceAccountFailure,
    updateAccountMcpServiceAccountRequest,
    updateAccountMcpServiceAccountSuccess,
} from 'src/openapi/admin/apis/UpdateAccountMcpServiceAccountApi';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {McpContract} from 'src/openapi/admin/models/McpContract';
import type {McpCreditAlertSettings} from 'src/openapi/admin/models/McpCreditAlertSettings';
import type {AppState} from 'src/store/typedef';

type McpState = {
    contracts: McpContract[] | null,
    contractsLoading: boolean,
    saving: boolean,
    mcpClientId: string | null,
    mcpClientIdLoading: boolean,
    alertSettings: McpCreditAlertSettings | null,
    alertSettingsLoading: boolean,
    alertSaving: boolean,
    alertError: ApiError | null,
};

const initialState: McpState = {
    contracts: null,
    contractsLoading: false,
    saving: false,
    mcpClientId: null,
    mcpClientIdLoading: false,
    alertSettings: null,
    alertSettingsLoading: false,
    alertSaving: false,
    alertError: null,
};

export const mcpSlice = createSlice({
    name: 'mcp',
    initialState,
    reducers: {
        clearMcpData: () => initialState,
    },
    extraReducers: builder => {
        builder
            .addCase(getMcpContractsRequest, state => {
                if (state.contracts === null) {
                    state.contractsLoading = true;
                }
            })
            .addCase(getMcpContractsSuccess, (state, action) => {
                state.contracts = action.payload.data;
                state.contractsLoading = false;
            })
            .addCase(getMcpContractsFailure, state => {
                state.contractsLoading = false;
            })
            .addCase(createMcpContractRequest, state => {
                state.saving = true;
            })
            .addCase(createMcpContractSuccess, state => {
                state.saving = false;
            })
            .addCase(createMcpContractFailure, state => {
                state.saving = false;
            })
            .addCase(updateMcpContractRequest, state => {
                state.saving = true;
            })
            .addCase(updateMcpContractSuccess, (state, action) => {
                const {contractId, mcpUpdateContractRequest} = action.payload.meta;
                if (state.contracts) {
                    const index = state.contracts.findIndex(c => c.contractId === contractId);
                    if (index !== -1) {
                        state.contracts[index] = {
                            ...state.contracts[index],
                            contractNumber: mcpUpdateContractRequest.contractNumber,
                            startDate: mcpUpdateContractRequest.startDate,
                            endDate: mcpUpdateContractRequest.endDate ?? null,
                        };
                    }
                }
                state.saving = false;
            })
            .addCase(updateMcpContractFailure, state => {
                state.saving = false;
            })
            .addCase(updateMcpContractCreditsRequest, state => {
                state.saving = true;
            })
            .addCase(updateMcpContractCreditsSuccess, state => {
                state.saving = false;
            })
            .addCase(updateMcpContractCreditsFailure, state => {
                state.saving = false;
            })
            .addCase(getAccountMcpServiceAccountRequest, state => {
                state.mcpClientIdLoading = true;
            })
            .addCase(getAccountMcpServiceAccountSuccess, (state, action) => {
                state.mcpClientId = action.payload.data.value ?? null;
                state.mcpClientIdLoading = false;
            })
            .addCase(getAccountMcpServiceAccountFailure, state => {
                state.mcpClientIdLoading = false;
            })
            .addCase(updateAccountMcpServiceAccountRequest, state => {
                state.saving = true;
            })
            .addCase(updateAccountMcpServiceAccountSuccess, (state, action) => {
                state.mcpClientId = action.payload.meta.mcpServiceAccount.value ?? null;
                state.saving = false;
            })
            .addCase(updateAccountMcpServiceAccountFailure, state => {
                state.saving = false;
            })
            .addCase(getMcpCreditAlertSettingsRequest, state => {
                state.alertSettingsLoading = true;
            })
            .addCase(getMcpCreditAlertSettingsSuccess, (state, action) => {
                state.alertSettings = action.payload.data;
                state.alertSettingsLoading = false;
            })
            .addCase(getMcpCreditAlertSettingsFailure, (state, action) => {
                state.alertSettingsLoading = false;
                state.alertError = action.payload.data;
            })
            .addCase(saveMcpCreditAlertSettingsRequest, state => {
                state.alertSaving = true;
            })
            .addCase(saveMcpCreditAlertSettingsSuccess, state => {
                state.alertSaving = false;
            })
            .addCase(saveMcpCreditAlertSettingsFailure, (state, action) => {
                state.alertSaving = false;
                state.alertError = action.payload.data;
            });
    },
});

export const {clearMcpData} = mcpSlice.actions;

export const selectMcpContracts = (state: AppState): McpContract[] | null =>
    state.accountPage.tabs.mcp.contracts;

export const selectMcpContractsLoading = (state: AppState): boolean =>
    state.accountPage.tabs.mcp.contractsLoading;

export const selectMcpSaving = (state: AppState): boolean =>
    state.accountPage.tabs.mcp.saving;

export const selectMcpClientId = (state: AppState): string | null =>
    state.accountPage.tabs.mcp.mcpClientId;

export const selectMcpClientIdLoading = (state: AppState): boolean =>
    state.accountPage.tabs.mcp.mcpClientIdLoading;

export const selectMcpAlertSettings = (state: AppState): McpCreditAlertSettings | null =>
    state.accountPage.tabs.mcp.alertSettings;

export const selectMcpAlertSettingsLoading = (state: AppState): boolean =>
    state.accountPage.tabs.mcp.alertSettingsLoading;

export const selectMcpAlertSaving = (state: AppState): boolean =>
    state.accountPage.tabs.mcp.alertSaving;

export const selectMcpAlertError = (state: AppState): ApiError | null =>
    state.accountPage.tabs.mcp.alertError;
